// UniUI Offline Persistence Layer
// -------------------------------------------------------------
// Two_complementary_durability_mechanisms_so_the_app_survives_refresh,
// back/forward, and full browser close/reopen:
//
//  1. API GET response cache  -> IndexedDB `cache` store (already declared in
//     storage.ts_but previously unused). Wrapped by apiFetch so every successful
//     GET is persisted on the user's device and served back when the network is
//     unavailable. This_is_the_durable, SW-independent store.
//
//  2. Scroll-position memory   -> localStorage, keyed_per_route, so the page
//     returns the user to exactly where they left off.

import { indexedDB } from "./storage";

const SCROLL_KEY_PREFIX = "uniui_scroll_";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

// --- API response cache (IndexedDB) ------------------------------------------

function isCacheableJson(response: Response): boolean {
  if (!response || !response.ok) return false;
  const ct = response.headers.get("content-type") || "";
  return ct.includes("application/json");
}

/**
 * Persist a successful GET response for `url` in IndexedDB.
 * No-ops on the server or when storage is unavailable.
 */
export async function cacheApiResponse(
  url: string,
  data: unknown,
): Promise<void> {
  if (!isBrowser()) return;
  try {
    await indexedDB.set("cache", url, data);
  } catch {
    // Best-effort: never break the request path over a cache write failure.
  }
}

/**
 * Read a previously cached GET response for `url` from IndexedDB.
 * Returns null when nothing is cached or storage is unavailable.
 */
export async function getCachedApiResponse<T = unknown>(
  url: string,
): Promise<T | null> {
  if (!isBrowser()) return null;
  try {
    const record = (await indexedDB.get("cache", url)) as
      | { value?: T }
      | null
      | undefined;
    if (record && "value" in record)
      return (record as { value: T }).value ?? null;
    return null;
  } catch {
    return null;
  }
}

/**
 * Drop a single cached entry (e.g. after a mutation) so stale data is not
 * served on the next offline read.
 */
export async function invalidateApiCache(url: string): Promise<void> {
  if (!isBrowser()) return;
  try {
    await indexedDB.remove("cache", url);
  } catch {
    // ignore
  }
}

/**
 * Invalidate a cached API response (called before auth-protected requests to
 * ensure no stale data is served after login/refresh/logout).
 */
export async function invalidateApiResponse(url: string): Promise<void> {
  if (!isBrowser()) return;
  try {
    await indexedDB.remove("cache", url);
  } catch {}
}

/**
 * Best-effort full clear of the API cache (used on logout / "clear offline
 * data" actions). Never throws.
 */
export async function clearApiCache(): Promise<void> {
  if (!isBrowser()) return;
  try {
    await indexedDB.clearStore("cache");
  } catch {
    // ignore
  }
}

export function saveScrollPosition(route: string, y: number): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(`${SCROLL_KEY_PREFIX}${route}`, String(y));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function getScrollPosition(route: string): number {
  if (!isBrowser()) return 0;
  try {
    const raw = localStorage.getItem(`${SCROLL_KEY_PREFIX}${route}`);
    return raw ? parseInt(raw, 10) || 0 : 0;
  } catch {
    return 0;
  }
}

export { isCacheableJson };
